import { useEffect, useId, useMemo, useRef, useState } from 'react';
import type * as React from 'react';
import type { ChangeEvent, KeyboardEvent } from 'react';
import type { ReactNode } from 'react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { useScheduledCursorSearch } from '@/hooks/useScheduledCursorSearch';
import { useAuthStore } from '@/store/auth';
import { cn } from '@/lib/utils';
import type {
    InfiniteScrollListCursor,
    InfiniteScrollListPagination,
    InfiniteScrollListSearchTerms,
} from '@/components/ui/infinite-scroll-list.types';

export interface OmnisearchFetchParams<TCursor extends InfiniteScrollListCursor = number> {
    terms?: InfiniteScrollListSearchTerms;
    queryText?: string;
    limit: number;
    cursor?: TCursor;
}

export interface OmnisearchPage<TItem, TCursor extends InfiniteScrollListCursor = number> {
    items: ReadonlyArray<TItem>;
    pagination: InfiniteScrollListPagination<TCursor>;
}

export type OmnisearchFetchPage<TItem, TCursor extends InfiniteScrollListCursor = number> = (
    params: OmnisearchFetchParams<TCursor>
) => Promise<OmnisearchPage<TItem, TCursor>>;

export interface OmnisearchSelectProps<TItem, TCursor extends InfiniteScrollListCursor = number> {
    inputId?: string;
    label: string;
    placeholder: string;
    minQueryLength?: number;
    pageSize: number;
    fetchPage: OmnisearchFetchPage<TItem, TCursor>;
    getItemId: (item: TItem) => TCursor;
    getItemLabel: (item: TItem) => string;
    getItemFilterText: (item: TItem) => string;
    renderItemCard?: (item: TItem) => ReactNode;
    onSelect: (item: TItem) => void;
    onClearSelection?: () => void;
    selectedItemId: TCursor | null;
    selectedItemLabel?: string;
    resetToken?: string;
    disabled?: boolean;
    fetchOnEmptyQuery?: boolean;
    reserveMenuSpace?: boolean;
    overlayMenu?: boolean;
    menuViewportClassName?: string;
}

export function OmnisearchSelect<TItem, TCursor extends InfiniteScrollListCursor = number>(
    props: OmnisearchSelectProps<TItem, TCursor>
) {
    const componentRootElementRef = useRef<HTMLDivElement | null>(null);
    const generatedInputId: string = useId();
    const inputId: string = props.inputId ?? generatedInputId;
    const activeRoleAssignmentId: number | null = useAuthStore(
        (state) => state.activeRoleAssignmentId
    );
    void props.minQueryLength;
    const minimumQueryLength: number = 0;
    const extraResetToken: string = props.resetToken ?? '';
    const scheduledSearchResetToken: string = `role=${activeRoleAssignmentId ?? 'none'};${extraResetToken}`;
    void props.fetchOnEmptyQuery;
    const fetchOnEmptyQuery: boolean = true;
    const scheduledSearch = useScheduledCursorSearch<TItem, TCursor>({
        minQueryLength: minimumQueryLength,
        delayMs: 1000,
        pageSize: props.pageSize,
        resetToken: scheduledSearchResetToken,
        fetchPage: props.fetchPage,
        getItemFilterText: props.getItemFilterText,
        fetchOnEmptyQuery: fetchOnEmptyQuery,
    });

    const [isMenuOpen, setIsMenuOpen] = useState<boolean>(false);
    const [isSelectionConfirmed, setIsSelectionConfirmed] = useState<boolean>(false);

    const visibleItems: ReadonlyArray<TItem> = scheduledSearch.visibleItems;
    const items: ReadonlyArray<TItem> = scheduledSearch.items;
    const pagination: InfiniteScrollListPagination<TCursor> = scheduledSearch.pagination;
    const isLoading: boolean = scheduledSearch.isLoading;
    const errorMessage: string | null = scheduledSearch.errorMessage;
    const hasAnyInput: boolean = scheduledSearch.queryText.trim().length > 0;
    const canOpenMenuOnFocus: boolean = hasAnyInput || fetchOnEmptyQuery;
    const shouldShowMenu: boolean = (hasAnyInput || fetchOnEmptyQuery) && isMenuOpen;
    const shouldRenderMenuSlot: boolean =
        props.overlayMenu === true
            ? shouldShowMenu
            : props.reserveMenuSpace === true || shouldShowMenu;

    useEffect(() => {
        setIsMenuOpen(false);
        setIsSelectionConfirmed(false);
    }, [scheduledSearchResetToken]);

    useEffect(() => {
        const handleDocumentMouseDown = (event: MouseEvent): void => {
            const componentRootElement: HTMLDivElement | null = componentRootElementRef.current;
            if (componentRootElement === null) {
                return;
            }
            if (event.target instanceof Node && !componentRootElement.contains(event.target)) {
                setIsMenuOpen(false);
            }
        };
        document.addEventListener('mousedown', handleDocumentMouseDown);
        return () => {
            document.removeEventListener('mousedown', handleDocumentMouseDown);
        };
    }, []);

    const selectedLabel: string | null = useMemo(() => {
        if (props.selectedItemId === null) {
            return null;
        }
        if (props.selectedItemLabel !== undefined) {
            return props.selectedItemLabel;
        }
        const selectedItem: TItem | undefined = items.find(
            (item: TItem) => props.getItemId(item) === props.selectedItemId
        );
        return selectedItem ? props.getItemLabel(selectedItem) : null;
    }, [items, props, props.selectedItemId, props.selectedItemLabel]);

    return (
        <div
            className={cn('space-y-2', props.overlayMenu === true && 'relative')}
            ref={componentRootElementRef}
        >
            <label className="text-sm font-medium" htmlFor={inputId}>
                {props.label}
            </label>
            <div className="relative">
                <Input
                    id={inputId}
                    value={scheduledSearch.queryText}
                    placeholder={props.placeholder}
                    disabled={props.disabled}
                    className={
                        isSelectionConfirmed
                            ? 'border-green-600 focus-visible:ring-green-600 pr-10'
                            : 'pr-10'
                    }
                    onFocus={() => {
                        const shouldOpenMenu: boolean = props.disabled ? false : canOpenMenuOnFocus;
                        setIsMenuOpen(shouldOpenMenu);
                        setIsSelectionConfirmed(false);
                    }}
                    onKeyDown={(event: KeyboardEvent<HTMLInputElement>) => {
                        if (event.key !== 'Escape' || !isMenuOpen) {
                            return;
                        }
                        event.preventDefault();
                        setIsMenuOpen(false);
                    }}
                    onChange={(event: ChangeEvent<HTMLInputElement>) => {
                        const nextQueryText: string = event.target.value;
                        const hasAnyNextInput: boolean = nextQueryText.trim().length > 0;
                        scheduledSearch.setQueryText(nextQueryText);
                        setIsMenuOpen(hasAnyNextInput || fetchOnEmptyQuery);
                        setIsSelectionConfirmed(false);
                    }}
                />
                {(scheduledSearch.queryText.trim().length > 0 || props.selectedItemId !== null) &&
                    !props.disabled && (
                        <button
                            type="button"
                            aria-label="Clear selection"
                            className="absolute right-2 top-1/2 -translate-y-1/2 h-6 w-6 rounded-full border text-xs text-muted-foreground hover:text-foreground hover:bg-accent"
                            onMouseDown={(event: React.MouseEvent<HTMLButtonElement>) => {
                                event.preventDefault();
                            }}
                            onClick={() => {
                                scheduledSearch.setQueryText('');
                                setIsMenuOpen(false);
                                setIsSelectionConfirmed(false);
                                if (props.onClearSelection !== undefined) {
                                    props.onClearSelection();
                                }
                            }}
                        >
                            x
                        </button>
                    )}
            </div>

            {selectedLabel !== null && (
                <div className="text-sm text-muted-foreground">
                    Selected: <span className="font-medium">{selectedLabel}</span>
                </div>
            )}

            {errorMessage !== null && (
                <Alert variant="destructive">
                    <AlertDescription>{errorMessage}</AlertDescription>
                </Alert>
            )}

            {shouldRenderMenuSlot && (
                <div
                    className={cn(
                        'border rounded-md bg-background',
                        props.overlayMenu === true && 'absolute left-0 right-0 z-50 mt-1 shadow-lg',
                        !shouldShowMenu && 'invisible pointer-events-none'
                    )}
                    data-omnisearch-menu-slot="true"
                >
                    <div
                        className={cn(
                            'max-h-64 overflow-auto divide-y',
                            props.menuViewportClassName
                        )}
                        data-omnisearch-menu-viewport="true"
                    >
                        {visibleItems.length === 0 ? (
                            <div className="p-3 text-sm text-muted-foreground">
                                {isLoading ? 'Searching...' : 'No matches.'}
                            </div>
                        ) : (
                            visibleItems.map((item: TItem) => {
                                const itemId: TCursor = props.getItemId(item);
                                const isSelected: boolean = props.selectedItemId === itemId;
                                const itemLabel: string = props.getItemLabel(item);
                                return (
                                    <button
                                        key={itemId}
                                        type="button"
                                        data-omnisearch-item-id={String(itemId)}
                                        className={`w-full text-left p-3 text-sm hover:bg-accent ${
                                            isSelected ? 'bg-accent' : ''
                                        }`}
                                        onClick={() => {
                                            // UX: selecting a value confirms selection and closes the menu.
                                            scheduledSearch.setQueryText(itemLabel);
                                            setIsMenuOpen(false);
                                            setIsSelectionConfirmed(true);
                                            props.onSelect(item);
                                        }}
                                    >
                                        {props.renderItemCard !== undefined
                                            ? props.renderItemCard(item)
                                            : itemLabel}
                                    </button>
                                );
                            })
                        )}
                    </div>

                    {pagination.has_more && (
                        <div className="p-2 border-t flex justify-end">
                            <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                disabled={
                                    isLoading ||
                                    scheduledSearch.canLoadMore === false ||
                                    pagination.next_cursor === undefined
                                }
                                onClick={() => {
                                    scheduledSearch.loadMore();
                                }}
                            >
                                {isLoading ? 'Loading...' : 'Load more'}
                            </Button>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}
